/*
 * Stay or Go? — public tool. Single-page diagnostic.
 *
 * Behavioral parity with artifacts/prototypes/vendor-exit-diagnostic-v1.html:
 *  - Two context inputs (team size, subscription tier)
 *  - Eight questions; 7 use yes/no/unsure, 1 (userAdoption) uses 4 buckets
 *  - Live Stay Score panel updates as buyer answers
 *  - Results card appears once context + all 8 answers complete
 *  - Auto-submits exactly once on first complete render (`autoSubmitted` flag)
 *
 * State persists in localStorage under motr-stay-or-go-state-v1; the tool
 * rehydrates on mount so a buyer can refresh without losing progress.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '@shared/api/client';
import { recordEvent } from '@shared/api/events';
import { getClientId } from '@shared/clientId';
import {
  answeredCount,
  buildMirror,
  buildPayload,
  calcCost,
  calcPaybackAndValue,
  calcStayScore,
  calcSwitchCost,
  fmtMoney,
  verdictFor,
  type Question,
  type QuestionOption,
  type StayOrGoConfig,
  type ToolState,
} from './calc';
import {
  loadPublishedConfig,
  STATE_STORAGE_KEY,
  SUBMISSION_ID_KEY,
} from './config';
import './StayOrGoTool.css';

const DEFAULT_OPTIONS: QuestionOption[] = [
  { key: 'yes', label: 'Yes', tone: 'yes' },
  { key: 'no', label: 'No', tone: 'no' },
  { key: 'unsure', label: "Don't know", tone: 'unsure' },
];

function readPersistedState(): ToolState {
  const empty: ToolState = { team: null, tier: null, answers: {} };
  try {
    const raw = localStorage.getItem(STATE_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<ToolState> | null;
    if (!parsed || typeof parsed !== 'object') return empty;
    return {
      team: typeof parsed.team === 'string' ? parsed.team : null,
      tier: typeof parsed.tier === 'string' ? parsed.tier : null,
      answers:
        parsed.answers && typeof parsed.answers === 'object'
          ? { ...parsed.answers }
          : {},
    };
  } catch {
    return empty;
  }
}

function persistState(state: ToolState): void {
  try {
    localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage disabled */
  }
}

function genClientSubmissionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return (
    'cs-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  );
}

export default function StayOrGoTool() {
  const [config, setConfig] = useState<StayOrGoConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [state, setState] = useState<ToolState>(() => readPersistedState());
  const [submitError, setSubmitError] = useState<string | null>(null);
  const autoSubmittedRef = useRef(false);
  // Captures wall-clock duration from tool mount to first complete submit.
  // Stamped onto payload.computed.completionSeconds.
  const startTimeRef = useRef<number>(Date.now());

  // Fire 'view' event once on mount.
  useEffect(() => {
    void recordEvent({ tool_id: 'stay_or_go', event_type: 'view' });
  }, []);

  // Load published config (or fall back to defaults).
  useEffect(() => {
    let alive = true;
    loadPublishedConfig()
      .then((cfg) => {
        if (alive) setConfig(cfg);
      })
      .catch((err: unknown) => {
        if (alive) {
          setConfigError(err instanceof Error ? err.message : 'Failed to load.');
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  // Persist on every state change.
  useEffect(() => {
    persistState(state);
  }, [state]);

  const setTeam = useCallback((key: string) => {
    setState((s) => ({ ...s, team: key }));
  }, []);
  const setTier = useCallback((key: string) => {
    setState((s) => ({ ...s, tier: key }));
  }, []);
  const setAnswer = useCallback((qid: string, val: string) => {
    setState((s) => ({ ...s, answers: { ...s.answers, [qid]: val } }));
  }, []);

  // Derived calc — pure functions over (state, config).
  const derived = useMemo(() => {
    if (!config) return null;
    const total = config.questions.length;
    const answered = answeredCount(state, config.questions);
    const score = calcStayScore(state, config.questions);
    const verdict = verdictFor(score, config.thresholds, config.verdicts);
    const cost = calcCost(state, config.calc, config.teamSizes, config.spendTiers);
    const switchCost = calcSwitchCost(
      state,
      config.calc,
      config.teamSizes,
      config.spendTiers,
    );
    const value = calcPaybackAndValue(cost, switchCost, config.calc);
    const ready = answered === total && state.team !== null && state.tier !== null;
    return { total, answered, score, verdict, cost, switchCost, value, ready };
  }, [state, config]);

  // Auto-submit on first complete render.
  useEffect(() => {
    if (!config || !derived || !derived.ready) return;
    if (autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    const completionSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
    const payload = {
      ...buildPayload(state, config),
      computed: {
        ...buildPayload(state, config).computed,
        completionSeconds,
      },
    };
    void recordEvent({
      tool_id: 'stay_or_go',
      event_type: 'results_shown',
      data: { completion_seconds: completionSeconds, score: derived.score },
    });
    const clientId = getClientId();
    api
      .submit(clientId, {
        tool_id: 'stay_or_go',
        schema_version: 'v1',
        client_submission_id: genClientSubmissionId(),
        submitted_at: new Date().toISOString(),
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        referrer:
          typeof document !== 'undefined' ? document.referrer || undefined : undefined,
        payload,
      })
      .then((res) => {
        try {
          localStorage.setItem(SUBMISSION_ID_KEY, res.submission_id);
        } catch {
          /* storage disabled */
        }
      })
      .catch((err: unknown) => {
        // Queued for retry by the API client; surface a non-blocking note.
        if (err instanceof ApiError && !err.isRetryable) {
          setSubmitError('Your results are below — saving to our records failed.');
        } else {
          setSubmitError(
            "Saved locally. We'll send your results when the network recovers.",
          );
        }
      });
  }, [config, derived, state]);

  if (configError) {
    return (
      <div className="sg-page">
        <div className="sg-banner-error">
          Couldn't load the diagnostic: {configError}
        </div>
      </div>
    );
  }
  if (!config || !derived) {
    return (
      <div className="sg-page">
        <div className="sg-loading">Loading diagnostic…</div>
      </div>
    );
  }

  const { content, stats, questions, teamSizes, spendTiers, calc } = config;
  const { score, verdict, cost, switchCost, value, answered, total, ready } = derived;

  return (
    <div className="sg-page">
      <div className="sg-subnav">
        <span className="sg-subnav__tag">{content.navTag}</span>
        <div className="sg-subnav__actions">
          <a className="sg-subnav__link" href="/plan-builder">Plan Builder</a>
          <a className="sg-subnav__link" href="/ddw">Workbench</a>
        </div>
      </div>

      <section className="sg-hero" aria-labelledby="sg-hero-title">
        <div
          className="sg-hero__badge"
          dangerouslySetInnerHTML={{ __html: content.heroBadge }}
        />
        <h1
          id="sg-hero-title"
          className="sg-hero__title"
          dangerouslySetInnerHTML={{ __html: content.heroTitle }}
        />
        <p
          className="sg-hero__sub"
          dangerouslySetInnerHTML={{ __html: content.heroSub }}
        />
        <div
          className="sg-hero__meta"
          dangerouslySetInnerHTML={{ __html: content.heroMeta }}
        />
      </section>

      <div className="sg-stats">
        {stats.map((s, i) => (
          <div className="sg-stat" key={i}>
            <div className="sg-stat__num">{s.num}</div>
            <div className="sg-stat__label">{s.label}</div>
          </div>
        ))}
      </div>

      {submitError && <div className="sg-banner-error">{submitError}</div>}

      <div className="sg-wrap">
        <div>
          <ContextCard
            teamSizes={teamSizes}
            spendTiers={spendTiers}
            selectedTeam={state.team}
            selectedTier={state.tier}
            onSelectTeam={setTeam}
            onSelectTier={setTier}
          />

          <div>
            {questions.map((q, i) => (
              <QuestionCard
                key={q.id}
                question={q}
                index={i}
                total={questions.length}
                value={state.answers[q.id] ?? null}
                onAnswer={setAnswer}
              />
            ))}
          </div>

          {ready ? (
            <ResultsCard
              score={score}
              verdictLabel={verdict.label}
              verdictBody={verdict.body}
              verdictTone={verdict.tone}
              cost={cost!}
              switchCost={switchCost!}
              value={value}
              calcCfg={calc}
              tagsHit={questions
                .filter(
                  (q) =>
                    state.answers[q.id] && (q.weight[state.answers[q.id]] || 0) < 0,
                )
                .map((q) => q.tag)}
            />
          ) : (
            <Gate
              answered={answered}
              total={total}
              hasTeam={state.team !== null}
              hasTier={state.tier !== null}
            />
          )}
        </div>

        <ScorePanel
          ready={ready}
          score={score}
          verdict={verdict}
          answered={answered}
          total={total}
          hasContext={state.team !== null && state.tier !== null}
          cost={cost}
        />
      </div>

    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

interface ContextCardProps {
  teamSizes: StayOrGoConfig['teamSizes'];
  spendTiers: StayOrGoConfig['spendTiers'];
  selectedTeam: string | null;
  selectedTier: string | null;
  onSelectTeam(key: string): void;
  onSelectTier(key: string): void;
}

function ContextCard({
  teamSizes,
  spendTiers,
  selectedTeam,
  selectedTier,
  onSelectTeam,
  onSelectTier,
}: ContextCardProps) {
  return (
    <div className="sg-ctx">
      <div className="sg-ctx__title">Context</div>
      <div className="sg-ctx__headline">Tell us about the situation</div>
      <p className="sg-ctx__sub">
        Two quick inputs. Both calibrate the cost numbers below to your team — neither
        is shared.
      </p>

      <div className="sg-ctx__label" id="sg-team-label">
        Legal team size
      </div>
      <div
        className="sg-ctx__pills"
        role="radiogroup"
        aria-labelledby="sg-team-label"
        style={{ ['--sg-pill-cols' as string]: String(teamSizes.length) }}
      >
        {teamSizes.map((t) => (
          <button
            key={t.key}
            type="button"
            role="radio"
            aria-checked={selectedTeam === t.key}
            className={
              'sg-ctx__pill' +
              (selectedTeam === t.key ? ' sg-ctx__pill--selected' : '')
            }
            onClick={() => onSelectTeam(t.key)}
          >
            <span>{t.label}</span>
            <span className="sg-ctx__pill-sub">{t.sub}</span>
          </button>
        ))}
      </div>

      <div className="sg-ctx__label" id="sg-tier-label">
        Annual subscription tier (best estimate)
      </div>
      <div
        className="sg-ctx__pills"
        role="radiogroup"
        aria-labelledby="sg-tier-label"
        style={{ ['--sg-pill-cols' as string]: String(spendTiers.length) }}
      >
        {spendTiers.map((t) => (
          <button
            key={t.key}
            type="button"
            role="radio"
            aria-checked={selectedTier === t.key}
            className={
              'sg-ctx__pill' +
              (selectedTier === t.key ? ' sg-ctx__pill--selected' : '')
            }
            onClick={() => onSelectTier(t.key)}
          >
            <span>{t.label}</span>
            <span className="sg-ctx__pill-sub">{t.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface QuestionCardProps {
  question: Question;
  index: number;
  total: number;
  value: string | null;
  onAnswer(qid: string, val: string): void;
}

function QuestionCard({ question, index, total, value, onAnswer }: QuestionCardProps) {
  const opts = question.options ?? DEFAULT_OPTIONS;
  const isWorse = value !== null && (question.weight[value] || 0) < 0;
  const cardClass =
    'sg-qcard' +
    (value ? ' sg-qcard--answered' : '') +
    (isWorse ? ' sg-qcard--bad' : '');
  const groupId = `sg-q-${question.id}-label`;
  return (
    <div className={cardClass}>
      <div className="sg-qcard__num">
        Question {index + 1} of {total}
      </div>
      <div className="sg-qcard__question" id={groupId}>
        {question.q}
      </div>
      <div
        className="sg-qcard__stat"
        dangerouslySetInnerHTML={{ __html: question.stat }}
      />
      <div className="sg-qcard__options" role="radiogroup" aria-labelledby={groupId}>
        {opts.map((o) => {
          const selected = value === o.key;
          const cls =
            'sg-qcard__opt' +
            (selected ? ` sg-qcard__opt--selected sg-qcard__opt--${o.tone}` : '');
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={selected}
              className={cls}
              onClick={() => onAnswer(question.id, o.key)}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Gate({
  answered,
  total,
  hasTeam,
  hasTier,
}: {
  answered: number;
  total: number;
  hasTeam: boolean;
  hasTier: boolean;
}) {
  const need: string[] = [];
  if (!hasTeam) need.push('team size');
  if (!hasTier) need.push('subscription tier');
  if (answered < total) {
    const remaining = total - answered;
    need.push(`${remaining} more answer${remaining === 1 ? '' : 's'}`);
  }
  return (
    <div className="sg-gate">
      <strong>Almost there.</strong> Provide {need.join(', ')} to see your verdict and
      24-month cost projection.
    </div>
  );
}

interface ScorePanelProps {
  ready: boolean;
  score: number;
  verdict: { label: string; body: string };
  answered: number;
  total: number;
  hasContext: boolean;
  cost: { total: number } | null;
}

function ScorePanel({
  ready,
  score,
  verdict,
  answered,
  total,
  hasContext,
  cost,
}: ScorePanelProps) {
  const fillWidthPct = ready ? score * 10 : answered > 0 ? score * 10 : 0;
  let fillBg = 'var(--textMuted)';
  if (ready) {
    fillBg = score <= 3 ? 'var(--warning)' : score <= 6 ? 'var(--accent)' : 'var(--success)';
  }

  let verdictNode: React.ReactNode;
  let progress: string;
  if (ready) {
    verdictNode = (
      <>
        <strong>{verdict.label}.</strong> {verdict.body}
      </>
    );
    progress = `All ${total} answered.`;
  } else if (answered > 0) {
    verdictNode = 'Trending — keep going to see your verdict.';
    progress = `${answered} of ${total} answered`;
  } else {
    verdictNode = 'Answer the questions to see your verdict.';
    progress = `0 of ${total} answered`;
  }

  let costValue = '—';
  let costMeta = 'Set context above first.';
  if (cost && answered >= 4) {
    costValue = fmtMoney(cost.total);
    costMeta = '24-month total · subscription + adaptation + adoption';
  } else if (hasContext) {
    costMeta = 'Answer 4+ questions to project a number.';
  }

  return (
    <aside
      className="sg-score-panel"
      aria-live="polite"
      aria-label="Stay Score panel"
    >
      <div className="sg-score-panel__label">Stay Score</div>
      <div>
        <span className="sg-score-panel__value">{ready ? score : '—'}</span>
        <span className="sg-score-panel__suffix">/10</span>
      </div>
      <div className="sg-score-panel__bar">
        <div
          className="sg-score-panel__bar-fill"
          style={{ width: fillWidthPct + '%', background: fillBg }}
        />
      </div>
      <div className="sg-score-panel__verdict">{verdictNode}</div>
      <div className="sg-score-panel__divider" />
      <div className="sg-score-panel__cost-label">
        Cost of staying — next 24 months
      </div>
      <div className="sg-score-panel__cost-value">{costValue}</div>
      <div className="sg-score-panel__cost-meta">{costMeta}</div>
      <div className="sg-score-panel__progress">{progress}</div>
    </aside>
  );
}

interface ResultsCardProps {
  score: number;
  verdictLabel: string;
  verdictBody: string;
  verdictTone: 'warn' | 'mid' | 'good';
  cost: NonNullable<ReturnType<typeof calcCost>>;
  switchCost: NonNullable<ReturnType<typeof calcSwitchCost>>;
  value: ReturnType<typeof calcPaybackAndValue>;
  calcCfg: StayOrGoConfig['calc'];
  tagsHit: string[];
}

function ResultsCard({
  score,
  verdictLabel,
  verdictBody,
  verdictTone,
  cost,
  switchCost,
  value,
  calcCfg,
  tagsHit,
}: ResultsCardProps) {
  const cardClass =
    'sg-results' +
    (verdictTone === 'warn'
      ? ' sg-results--warn'
      : verdictTone === 'mid'
      ? ' sg-results--mid'
      : '');
  const efficiencyPct = Math.round(calcCfg.newVendorEfficiency * 100);
  const mirrorHtml = buildMirror(tagsHit, score);
  const escalationPct = Math.round(cost.priceEscalation * 100);
  const showLeaveLeaning = score <= 6;

  return (
    <div className={cardClass} role="region" aria-labelledby="sg-results-headline">
      <div className="sg-results__tag">Your Stay Score: {score}/10</div>
      <h2 id="sg-results-headline" className="sg-results__verdict">
        {verdictLabel}.
      </h2>
      <p className="sg-results__validation">{verdictBody}</p>

      <div className="sg-results__numbers">
        <div className="sg-num-card sg-num-card--warn">
          <div className="sg-num-card__label">Cost of staying — 24 months</div>
          <div className="sg-num-card__value">{fmtMoney(cost.total)}</div>
          <div className="sg-num-card__meta">
            Subscription + adaptation + adoption combined.
          </div>
        </div>
        <div className="sg-num-card sg-num-card--secondary">
          <div className="sg-num-card__label">Cost of switching — one-time</div>
          <div className="sg-num-card__value">{fmtMoney(switchCost.total)}</div>
          <div className="sg-num-card__meta">
            Implementation + parallel run + internal time + productivity dip.
          </div>
        </div>
      </div>

      <div className="sg-bd">
        <div className="sg-bd__row sg-bd__row--head">
          <span>Cost of staying — breakdown</span>
          <span />
        </div>
        <div className="sg-bd__row">
          <span>
            Subscription run-rate (24 mo, {escalationPct}% escalation)
          </span>
          <strong>{fmtMoney(cost.subscription24mo)}</strong>
        </div>
        <div className="sg-bd__row">
          <span>Adaptation tax (workarounds, configuration drag)</span>
          <strong>{fmtMoney(cost.adaptation24mo)}</strong>
        </div>
        {cost.adoption24mo > 0 && (
          <div className="sg-bd__row">
            <span>Adoption-decay productivity drag</span>
            <strong>{fmtMoney(cost.adoption24mo)}</strong>
          </div>
        )}
        <div className="sg-bd__row sg-bd__row--total">
          <span>Total — cost of staying</span>
          <strong>{fmtMoney(cost.total)}</strong>
        </div>

        <div className="sg-bd__row sg-bd__row--head-mid">
          <span>Cost of switching — breakdown</span>
          <span />
        </div>
        <div className="sg-bd__row">
          <span>
            New vendor implementation ({Math.round(calcCfg.switchImplPctOfAnnual * 100)}% of annual subscription)
          </span>
          <strong>{fmtMoney(switchCost.implementation)}</strong>
        </div>
        <div className="sg-bd__row">
          <span>
            Parallel run ({calcCfg.switchParallelMonths} mo of dual subscription)
          </span>
          <strong>{fmtMoney(switchCost.parallelRun)}</strong>
        </div>
        <div className="sg-bd__row">
          <span>Internal time (project mgmt, training, migration)</span>
          <strong>{fmtMoney(switchCost.internalTime)}</strong>
        </div>
        <div className="sg-bd__row">
          <span>
            Productivity dip during transition ({Math.round(calcCfg.switchProdDipPct * 100)}%, {calcCfg.switchProdDipMonths} mo)
          </span>
          <strong>{fmtMoney(switchCost.productivityDip)}</strong>
        </div>
        <div className="sg-bd__row sg-bd__row--total">
          <span>Total — cost of switching</span>
          <strong>{fmtMoney(switchCost.total)}</strong>
        </div>
      </div>

      {value && (
        <div className="sg-results__numbers">
          <div className="sg-num-card sg-num-card--positive">
            <div className="sg-num-card__label">Payback period</div>
            <div className="sg-num-card__value" style={{ color: 'var(--secondary)' }}>
              ~ month {value.paybackMonths}
            </div>
            <div className="sg-num-card__meta">
              Cumulative savings exceed the one-time switch cost.
            </div>
          </div>
          <div className="sg-num-card sg-num-card--positive">
            <div className="sg-num-card__label">3-year net value of switching</div>
            <div className="sg-num-card__value" style={{ color: 'var(--secondary)' }}>
              {value.threeYearValue >= 0
                ? fmtMoney(value.threeYearValue)
                : '−' + fmtMoney(-value.threeYearValue)}
            </div>
            <div className="sg-num-card__meta">
              Assumes a well-selected replacement delivers {efficiencyPct}% lower TCO. Tunable in admin.
            </div>
          </div>
        </div>
      )}

      <div
        className="sg-mirror"
        dangerouslySetInnerHTML={{ __html: mirrorHtml }}
      />

      <div className="sg-cta-row">
        {showLeaveLeaning ? (
          <>
            <a className="sg-cta-row__primary" href="/ddw">
              Evaluate alternatives →
            </a>
            <a className="sg-cta-row__secondary" href="/plan-builder">
              Or fix the operational state first
            </a>
          </>
        ) : (
          <>
            <a className="sg-cta-row__primary" href="/plan-builder">
              Build the plan to extract more value →
            </a>
            <a className="sg-cta-row__secondary" href="/ddw">
              Or run a parallel evaluation just in case
            </a>
          </>
        )}
      </div>
    </div>
  );
}
